import React, { useEffect, useState, useRef, useContext, useCallback, useMemo, memo } from "react";
import { useTheme, styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Unstable_Grid2";
import Divider from "@mui/material/Divider";
import Input from "@mui/material/Input";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { Responsive, WidthProvider } from "react-grid-layout";
import clsx from "clsx";
import grey from "@mui/material/colors/grey";
import "react-grid-layout/css/styles.css";
import {
  NewWidgetDialog,
  ShareTemplateDialog,
  ShareDashboardDialog,
  DashboardPropertiesDialog  
} from "containers";
import ArrowLeftIcon from "@mui/icons-material/ArrowBackIos";
import ArrowRightIcon from "@mui/icons-material/ArrowForwardIos";
import IconForProperties from "@mui/icons-material/ListAlt";
import StarIcon from "@mui/icons-material/Star";
import DuplicateIcon from "@mui/icons-material/FileCopy";
import ShareIcon from "@mui/icons-material/Share";
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CreateIcon from "@mui/icons-material/Create";
import { default as cloneDeep } from "lodash/cloneDeep";
import ISXUtils, {
  ISXContext,
  DashboardsContext,
  DRAWER_WIDTH,
  APP_BAR_HEIGHT,
} from "services/Utils";
import DashboardLock from "./DashboardLock";

const RELOAD_PAGE_HOUR = 4;

const ResponsiveReactGridLayout = WidthProvider(memo(Responsive));

const defaultProps = {
  className: "layout",
  rowHeight: 30,
  breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
  cols: { lg: 12, md: 10, sm: 6, xs: 4, xxs: 1 },
  layouts: {},
};

// const DashboardGrid = styled(Grid)({
//   flexDirection: "row",
//   justifyContent: "flex-start",
//   alignItems: "center",
//   paddingTop: 8,
//   paddingLeft: 0,
//   paddingBottom: 4,
//   paddingRight: 4,
//   minHeight: 56,
//   transition,
// });

const HeaderButton = styled(IconButton)({
  width: 40,
  height: 40,
  padding: 0,
});

const SharedBadge = styled(GroupIcon)({
  color: grey[500],
  width: 14,
});

const useStyles = makeStyles((theme) => {
  const transition = theme.transitions.create("margin-left", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  });

  return {
    // grid: {
    //   flexDirection: "row",
    //   justifyContent: "flex-start",
    //   alignItems: "center",
    //   paddingTop: 8,
    //   paddingLeft: 0,
    //   paddingBottom: 4,
    //   paddingRight: 4,
    //   minHeight: 56,
    //   transition,
    // },
    // topBarShift: {
    //   width: `calc(100% - ${DRAWER_WIDTH}px)`,
    //   marginLeft: DRAWER_WIDTH,
    //   [theme.breakpoints.down("md")]: {
    //     width: "100%",
    //     marginLeft: "calc(100% - 44px)",
    //   },
    //   transition,
    // },
    dividerShift: {
      [theme.breakpoints.down("md")]: {
        visibility: "hidden",
      },
    },
    dashboardContainer: {
      height: `calc(100dvh - ${APP_BAR_HEIGHT + 57}px)`,
      width: "100%",
      overflow: "auto",
      position: "relative",
      transition,
    },
    dashboardContainerShift: {
      marginLeft: DRAWER_WIDTH,
      width: `calc(100% - ${DRAWER_WIDTH}px)`,
      [theme.breakpoints.down("md")]: {
        marginLeft: "calc(100% - 44px)",
        width: 44,
        overflow: "hidden",
      },
      transition,
    },
    dashboardMask: {
      zIndex: 1000,
      height: "100%",
      width: 44,
      position: "absolute",
      top: 0,
    },
  };
});

const Dashboard = (props) => {
  const [title, setTitle] = useState("");
  const [locked, setLocked] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openDashboardPropertiesDialog, setOpenDashboardPropertiesDialog] =
    useState(false);

  const titleTimeout = useRef(null);
  const titleInput = useRef(null);
  const refreshTimeout = useRef(null);
  const previousDashboardTitle = useRef(null);

  const isxContext = useContext(ISXContext);
  const { loading, setLoading, drawerOpen, setDrawerOpen } =
    useContext(DashboardsContext);

  const theme = useTheme();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));

  const dashboardTitle = (props.dashboard || {}).title || "";

  const editTitle = () => {
    setAnchorEl(null);
    setTimeout(() => titleInput.current.focus(), 500);
  };

  const checkTime = () => {
    const dd = new Date();
    if (dd.getHours() === RELOAD_PAGE_HOUR) {
      console.log("reload location", dd);
      window.location.reload();
    }
    refreshTimeout.current = setTimeout(checkTime, 3600000);
  };

  useEffect(() => {
    // start a timer to trigger checktimer at the start of next hour.
    //CheckTime will check every hour and when the hour matches the RELOAD_PAGE_HOUR,
    // it will reload the page, which in turn will check for new code updates via serviceworker
    refreshTimeout.current = setTimeout(
      checkTime,
      (60 - new Date().getMinutes()) * 60000
    );
    console.log("set timer on start", refreshTimeout.current);
    return () => {
      clearTimeout(refreshTimeout.current);
    };
  }, []);

  useEffect(() => {
    setLoading(false);
  }, [setLoading]);

  const duplicateDashboard = (patch, groups) => {
    const { title, widgets_layout } = props.dashboard;
    // have to make deep copies of widgets and layout
    const clonedWidgetsLayout = cloneDeep(widgets_layout);
    const clonedWidgets = cloneDeep(widgets);
    const template = {
      title,
      widgets: clonedWidgets,
      widgets_layout: clonedWidgetsLayout,
    };
    const duplicate = ISXUtils.copyDashboardAsTemplate(template);
    props.addDashboard(duplicate, patch, groups);
    
  };

  useEffect(() => {
    if (props.dashboard && dashboardTitle !== previousDashboardTitle.current) {
      previousDashboardTitle.current = dashboardTitle;
      document.title = `${dashboardTitle || "Unnamed"} | ${isxContext.labels.organization
        } Dashboard`;
      setTitle(dashboardTitle);
    }
  }, [props.dashboard, dashboardTitle, isxContext.labels.organization]);

  const handleConfigureMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleConfigureMenuClose = () => {
    setAnchorEl(null);
  };

  const setAsDefault = () => {
    setAnchorEl(null);
    props.setAsDefault();
  };

  const setDashboardTitle = (newTitle) => {
    setTitle(newTitle);
    clearTimeout(titleTimeout.current);
    titleTimeout.current = setTimeout(() => writebackDashboardTitle(), 3000);
  };

  const writebackDashboardTitle = () => {
    clearTimeout(titleTimeout.current);
    if (dashboardTitle !== title) {
      props.setDashboardTitle(title);
      previousDashboardTitle.current = title;
    }
  };

  const onDashboardChange = props.onDashboardChange;
  const onLayoutChange = useCallback(
    (layout, layouts) => {
      if (!locked) {
        // when layout gets updated remotely, the dashboard can't distinguish from
        //  a local change; check lock before attempting write
        onDashboardChange(layouts);
      }
    },
    [locked, onDashboardChange]
  );

  const handleConfirmDialog = (event) => {
    setAnchorEl(null);
    setOpenConfirmDialog(true);
  };

  const removeConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleDashboardPropertiesDialogOpen = (event) => {
    setAnchorEl(null);
    setOpenDashboardPropertiesDialog(true);
  };

  const handleDashboardPropertiesDialogClose = (event) => {
    setOpenDashboardPropertiesDialog(false);
  };
    

  const {
    isOwner,
    isDashboardAdmin,
    addCard,
    removeWidget,
    openAddWidgetDialog,
    removeAddWidgetDialog,
    // openManageDashboardDialog,
    readOnly = false,
  } = props;
  const classes = useStyles();

  const widgetIds = React.useMemo(
    () => props.dashboard.widgets || [],
    [props.dashboard.widgets]
  );
  const widgets = React.useMemo(
    () => widgetIds.map((guuid) => props.widgets[guuid]),
    [widgetIds, props.widgets]
  );
  const toggleDrawerOpen = () => {
    setDrawerOpen((drawerOpen) => !drawerOpen);
  };

  const renderArrowIcon = (drawerOpen) => {
    // const ArrowIcon = drawerOpen ? ArrowLeftIcon : ArrowRightIcon;
    // return <ArrowIcon style={{ fontSize: "20px" }} />;

    // NOTE: for some reason v4 icons asymmetric so hack to fix for now
    return drawerOpen ? (
      <ArrowLeftIcon
        style={{ fontSize: "20px", marginLeft: 4, marginRight: -4 }}
      />
    ) : (
      <ArrowRightIcon style={{ fontSize: "20px" }} />
    );
  };

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [drawerOpen]);

  const widgetChildren = useMemo(
    () =>
      widgets.map((w) => {
        const widgetType = w.type;
        const widgetContents =
          isxContext.widgets[widgetType] || isxContext.widgets.Unsupported;
        const widgetProps = widgetContents.properties || {};
        const minHeight = widgetProps.minHeight || 8;
        const minWidth = widgetProps.minWidth || 4;
        const defaultHeight = widgetProps.defaultHeight || minHeight;
        const defaultWidth = widgetProps.defaultWidth || minWidth;
        return (
          <div
            key={w.guuid}
            data-grid={{
              x: 0,
              y: 0,
              h: defaultHeight,
              w: defaultWidth,
              minH: minHeight,
              minW: minWidth,
            }}
          >
            <widgetContents.component
              widget={w}
              removeWidget={removeWidget}
              readOnly={readOnly || locked}
            />
          </div>
        );
      }),
    [isxContext.widgets, locked, readOnly, removeWidget, widgets]
  );
  return (
    <div id="dashboard" style={{ overflow: "hidden" }}>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          className={clsx(drawerOpen && classes.dashboardContainerShift)}
          minHeight={`calc(100dvh - ${APP_BAR_HEIGHT}px)`}
        >
          <CircularProgress disableShrink size="4rem" color="primary" />
        </Box>
      ) : (
        <>
          <Grid
            container
            sx={{
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              paddingTop: 1,
              paddingLeft: 0,
              paddingBottom: 0.5,
              paddingRight: 0.5,
              minHeight: 56,
              width: drawerOpen
                ? { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` }
                : "100%",
              marginLeft: drawerOpen
                ? { xs: "calc(100% - 44px)", md: DRAWER_WIDTH / 8 }
                : 0,
              transition: theme.transitions.create("margin-left", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }}
            // className={clsx(classes.grid, drawerOpen && classes.topBarShift)}
          >
            <Grid container xs={8} alignItems="center">
              {!readOnly && (
                <Grid
                  style={{ paddingLeft: "0.25rem", paddingRight: "0.125rem" }}
                >
                  <IconButton size="large" aria-label="Menu" onClick={toggleDrawerOpen}>
                    {renderArrowIcon(drawerOpen)}
                  </IconButton>
                </Grid>
              )}
              <Grid
                style={{
                  flex: 1,
                  display: drawerOpen && isSmallerScreen ? "none" : "block",
                }}
              >
                {!readOnly && !locked ? (
                  <Input
                    inputRef={titleInput}
                    placeholder="Unnamed Dashboard"
                    disableUnderline={true}
                    variant="h6"
                    color="secondary"
                    value={title}
                    onChange={(e) => setDashboardTitle(e.target.value)}
                    onBlur={writebackDashboardTitle}
                    style={{
                      width: "100%",
                    }}
                  />
                ) : (
                  <Typography
                    noWrap
                    {...(!title && { color: "textSecondary" })}
                    style={{
                      fontSize: "1em",
                    }}
                  >
                    {title || "Unnamed Dashboard"}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Grid
              container
              xs={4}
              justifyContent="flex-end"
              alignItems="center"
              style={{
                display: drawerOpen && isSmallerScreen ? "none" : "flex",
              }}
            >
              {!readOnly && !locked && (
                <IconButton className={classes.headerButtons} onClick={addCard}>
                  <AddCircleIcon />
                </IconButton>
              )}
              {!readOnly &&
                (props.dashboard.acu_groups?.length > 0 ? (
                  <Badge
                    overlap="circular"
                    badgeContent={
                      <Tooltip title="Dashboard is shared">
                        <SharedBadge />
                      </Tooltip>
                    }
                  >
                    <DashboardLock
                      guuid={props.dashboard.guuid}
                      locked={locked}
                      setLocked={setLocked}
                    ></DashboardLock>
                  </Badge>
                ) : (
                  <DashboardLock
                    guuid={props.dashboard.guuid}
                    locked={locked}
                    setLocked={setLocked}
                  ></DashboardLock>
                ))}
              {!readOnly && (
                <IconButton
                  className={classes.headerButtons}
                  aria-owns={anchorEl ? "dashboard-configure-menu" : null}
                  aria-haspopup="true"
                  onClick={handleConfigureMenu}
                >
                  <MoreVertIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
          <Divider />
          <Box
            className={clsx(
              classes.dashboardContainer,
              drawerOpen && classes.dashboardContainerShift
            )}
          >
            <Box width={drawerOpen ? "100dvw" : "100%"}>
              <ResponsiveReactGridLayout
                {...defaultProps}
                measureBeforeMount={false}
                onLayoutChange={onLayoutChange}
                layouts={props.widgetsLayout}
                draggableCancel="input,button"
                draggableHandle=".draghandle"
                isDraggable={!readOnly && !locked}
                isResizable={!readOnly && !locked}
              >
                {widgetChildren}
              </ResponsiveReactGridLayout>
            </Box>
            {drawerOpen && isSmallerScreen && (
              <Box
                className={classes.dashboardMask}
                onClick={toggleDrawerOpen}
              />
            )}
          </Box>
          <NewWidgetDialog
            open={openAddWidgetDialog}
            guuid={props.guuid}
            removeDialog={removeAddWidgetDialog}
          />
          <Menu
            id="dashboard-config-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            onClose={handleConfigureMenuClose}
          >
            <MenuItem onClick={setAsDefault}>
              <ListItemIcon>
                <StarIcon />
              </ListItemIcon>
              <ListItemText inset primary="Set as Default" />
            </MenuItem>
            <MenuItem onClick={handleDashboardPropertiesDialogOpen}>
              <ListItemIcon>
                <IconForProperties/>
              </ListItemIcon>              
              <ListItemText inset primary={locked ? "Copy Dashboard" : "Manage/Copy/Share Dashboard"} />
            </MenuItem>
        {!locked && isDashboardAdmin && (
          <MenuItem onClick={handleConfirmDialog}>
          <ListItemIcon>
          <CancelIcon />
          </ListItemIcon>
          <ListItemText inset primary="Delete this Dashboard" />
          </MenuItem>
        )}
          </Menu>
          <Dialog
            open={openConfirmDialog}
            onClose={removeConfirmDialog}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
          >
            <DialogTitle id="confirm-dialog-title">
              {"Are you sure that you want to remove this dashboard?"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="confirm-dialog-description">
                Press the confirm button to finalize the removal of this
                dashboard.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={props.removeDashboard} color="primary" autoFocus>
                Confirm
              </Button>
              <Button onClick={removeConfirmDialog} color="primary">
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
          <DashboardPropertiesDialog
            dashboard={props.dashboard}
            widgets={props.widgets}
            open={openDashboardPropertiesDialog}
            locked={locked}
            owner={isOwner}
            duplicateDashboard={duplicateDashboard}
            handleClose={handleDashboardPropertiesDialogClose}
          />
        </>
      )}
    </div>
  );
};

export default Dashboard;
